// ============================================================================
// Auth Store - Zustand State Management with Cognito Integration
// EduPath Africa v2.0
// ============================================================================

import { create } from 'zustand';
import { preCacheAllQuestionBanks } from '@/services/questionPreCache';
import { persist, createJSONStorage } from 'zustand/middleware';
import { normalizeRole } from '@/lib/roleConfig';
import type { UserRole, TierType } from '@/lib/roleConfig';
import { cognitoService, type RegisterParams } from '@/services/cognitoService';

// ============================================================================
// Types
// ============================================================================

export interface AuthUser {
  id: string;
  sub?: string; // Cognito subject ID (same as id)
  email: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  role: UserRole;
  schoolId?: string;
  grade?: number;
  entitlementLevel: TierType;
  tier?: TierType; // Alias for entitlementLevel
  createdAt?: string;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  grade?: number;
  schoolId?: string;
  role?: string;
}

interface AuthState {
  // State
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isHydrated: boolean;
  error: string | null;
  pendingVerificationEmail: string | null;
  
  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  confirmEmail: (email: string, code: string) => Promise<void>;
  resendCode: (email: string) => Promise<void>;
  logout: () => void;
  refreshAuth: () => Promise<boolean>;
  setUser: (user: AuthUser | null) => void;
  updateUser: (updates: Partial<AuthUser>) => void;
  setTokens: (accessToken: string, refreshToken?: string) => void;
  clearError: () => void;
  reset: () => void;
  setHydrated: () => void;
  initializeFromSession: () => Promise<void>;
}

// ============================================================================
// Token Parsing
// ============================================================================

interface JWTPayload {
  sub: string;
  email: string;
  'custom:role'?: string;
  'custom:schoolId'?: string;
  'custom:grade'?: string;
  'custom:firstName'?: string;
  'custom:lastName'?: string;
  'custom:tier'?: string;
  'cognito:groups'?: string[];
  exp: number;
}

function parseJWT(token: string): JWTPayload | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return payload;
  } catch {
    return null;
  }
}

function extractUserFromToken(token: string): AuthUser | null {
  const payload = parseJWT(token);
  if (!payload) return null;
  
  // Determine role from custom attribute or cognito groups
  let role = payload['custom:role'] || 'LEARNER';
  if (payload['cognito:groups']?.includes('admin')) {
    role = 'ADMIN';
  } else if (payload['cognito:groups']?.includes('teacher')) {
    role = 'TEACHER';
  } else if (payload['cognito:groups']?.includes('guardian')) {
    role = 'GUARDIAN';
  }
  
  return {
    id: payload.sub,
    sub: payload.sub,
    email: payload.email,
    firstName: payload['given_name'] || payload['custom:firstName'],
    lastName: payload['family_name'] || payload['custom:lastName'],
    role: normalizeRole(role),
    schoolId: payload['custom:schoolId'],
    grade: payload['custom:grade'] ? parseInt(payload['custom:grade'], 10) : undefined,
    entitlementLevel: (payload['custom:entitlementLevel'] as TierType) || 'basic',
  };
}

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  isHydrated: false,
  error: null,
  pendingVerificationEmail: null,
};

// ============================================================================
// Store Definition
// ============================================================================

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // ------------------------------------------------------------------
      // Initialize from existing Cognito session
      // ------------------------------------------------------------------
      initializeFromSession: async () => {
        try {
          const session = await cognitoService.getCurrentSession();
          if (session) {
            const user = extractUserFromToken(session.idToken);
            if (user) {
              set({
                user,
                accessToken: session.idToken,  // API Gateway Cognito authorizer requires ID token
                refreshToken: session.refreshToken,
                isAuthenticated: true,
                isHydrated: true,
              });
              return;
            }
          }
        } catch (error) {
          console.error('Failed to initialize from session:', error);
        }
        set({ isHydrated: true });
      },

      // ------------------------------------------------------------------
      // Login Action
      // ------------------------------------------------------------------
      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        
        try {
          const result = await cognitoService.signIn(email, password);
          
          const user = extractUserFromToken(result.idToken);
          if (!user) {
            throw new Error('Failed to parse user from token');
          }
          
          set({
            user,
            accessToken: result.idToken,  // API Gateway Cognito authorizer requires ID token
            refreshToken: result.refreshToken,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            pendingVerificationEmail: null,
          });
          // Sprint 3: Pre-cache question banks for offline use
          preCacheAllQuestionBanks().catch(console.warn);
          
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Login failed';
          
          // Check if user needs to verify email
          if (message.includes('verify your email')) {
            set({
              isLoading: false,
              error: message,
              pendingVerificationEmail: email,
            });
          } else {
            set({
              isLoading: false,
              error: message,
            });
          }
          throw error;
        }
      },

      // ------------------------------------------------------------------
      // Register Action
      // ------------------------------------------------------------------
      register: async (data: RegisterData) => {
        set({ isLoading: true, error: null });
        
        try {
          const registerParams: RegisterParams = {
            email: data.email,
            password: data.password,
            firstName: data.firstName,
            lastName: data.lastName,
            phone: data.phone,
            role: data.role || 'LEARNER',
            grade: data.grade,
          };
          
          await cognitoService.signUp(registerParams);
          
          // Registration successful - user needs to verify email
          set({
            isLoading: false,
            error: null,
            pendingVerificationEmail: data.email,
          });
          
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Registration failed';
          set({
            isLoading: false,
            error: message,
          });
          throw error;
        }
      },

      // ------------------------------------------------------------------
      // Confirm Email
      // ------------------------------------------------------------------
      confirmEmail: async (email: string, code: string) => {
        set({ isLoading: true, error: null });
        
        try {
          await cognitoService.confirmSignUp(email, code);
          
          set({
            isLoading: false,
            error: null,
            pendingVerificationEmail: null,
          });
          // Sprint 3: Pre-cache question banks for offline use
          preCacheAllQuestionBanks().catch(console.warn);
          
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Verification failed';
          set({
            isLoading: false,
            error: message,
          });
          throw error;
        }
      },

      // ------------------------------------------------------------------
      // Resend Verification Code
      // ------------------------------------------------------------------
      resendCode: async (email: string) => {
        set({ isLoading: true, error: null });
        
        try {
          await cognitoService.resendConfirmationCode(email);
          set({ isLoading: false });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to resend code';
          set({
            isLoading: false,
            error: message,
          });
          throw error;
        }
      },

      // ------------------------------------------------------------------
      // Logout Action
      // ------------------------------------------------------------------
      logout: () => {
        cognitoService.signOut();
        set({
          ...initialState,
          isHydrated: true, // Keep hydrated after logout
        });
      },

      // ------------------------------------------------------------------
      // Refresh Auth
      // ------------------------------------------------------------------
      refreshAuth: async () => {
        try {
          const result = await cognitoService.refreshSession();
          
          const user = extractUserFromToken(result.idToken);
          if (!user) {
            throw new Error('Failed to parse user from token');
          }
          
          set({
            user,
            accessToken: result.idToken,  // API Gateway Cognito authorizer requires ID token
            refreshToken: result.refreshToken,
            isAuthenticated: true,
          });
          
          return true;
        } catch (error) {
          console.error('Token refresh failed:', error);
          // DON'T logout here - let the API 401 handler deal with it
          // This prevents logout during navigation when Cognito session is temporarily unavailable
          return false;
        }
      },

      // ------------------------------------------------------------------
      // Utility Actions
      // ------------------------------------------------------------------
      setUser: (user) => set({ user }),
      
      updateUser: (updates) => set((state) => ({
        user: state.user ? { ...state.user, ...updates } : null,
      })),
      
      setTokens: (accessToken, refreshToken) => set({
        accessToken,
        refreshToken: refreshToken || get().refreshToken,
        isAuthenticated: true,
      }),
      
      clearError: () => set({ error: null }),
      
      reset: () => set({ ...initialState, isHydrated: true }),
      
      setHydrated: () => set({ isHydrated: true }),
    }),
    {
      name: 'edupath-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Mark as hydrated first
          state.setHydrated();
          
          // Only try to restore from Cognito if we DON'T have valid state
          // This prevents overwriting good state with failed session lookups
          if (!state.isAuthenticated || !state.accessToken) {
            state.initializeFromSession();
          }
          // If we already have auth state from localStorage, trust it
          // The API will 401 if tokens are actually invalid
        }
      },
    }
  )
);

// ============================================================================
// Selectors / Hooks
// ============================================================================

export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useUser = () => useAuthStore((state) => state.user);
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthError = () => useAuthStore((state) => state.error);
export const useAuthReady = () => useAuthStore((state) => state.isHydrated);

export default useAuthStore;
